import React, { useState, useRef } from 'react'
import { formatTimeHorizon } from './timeHorizon.js'
import { Button, TextArea, Banner, Pill } from '../ui'
import { GoalStatusLabel } from './GoalStatusLabel.jsx'

const TYPE_BADGE = {
  delivery:    { background: '#e89a5d18', color: '#8a5a2a' },
  development: { background: '#3b82c418', color: '#1e4a7a' },
}

export function GoalHeader({
  goal,
  onUpdateGoal,
  onActivate,
  onAchieve,
  onDrop,
  onReopen,
  onSetStatusLabel,
}) {
  const [showDropForm, setShowDropForm] = useState(false)
  const [dropReason, setDropReason] = useState('')
  const [dropping, setDropping] = useState(false)
  const [dropError, setDropError] = useState(null)
  const titleRef = useRef(null)
  const descRef = useRef(null)

  const typeBadge = TYPE_BADGE[goal.goalType] || TYPE_BADGE.delivery
  const isEditable = goal.status !== 'achieved' && goal.status !== 'dropped'

  function handleTitleBlur() {
    const text = titleRef.current?.innerText?.trim()
    if (text && text !== goal.title) onUpdateGoal?.(goal.goalId, { title: text })
  }

  function handleDescBlur() {
    const text = descRef.current?.innerText?.trim() || ''
    if (text !== (goal.description || '')) onUpdateGoal?.(goal.goalId, { description: text })
  }

  async function handleDrop() {
    setDropError(null)
    if (dropReason.trim().length < 10) { setDropError('Reason must be at least 10 characters.'); return }
    setDropping(true)
    try {
      await onDrop?.(goal.goalId, dropReason.trim())
      setShowDropForm(false)
      setDropReason('')
    } catch (err) {
      setDropError(err?.message || 'Failed to drop goal.')
    } finally {
      setDropping(false)
    }
  }

  return (
    <div style={styles.header}>
      <div style={styles.topRow}>
        <div style={styles.badges}>
          <span style={{ ...styles.typeBadge, ...typeBadge }}>{goal.goalType}</span>
          {goal.timeHorizon && <span style={styles.horizon}>{formatTimeHorizon(goal.timeHorizon)}</span>}
        </div>
        {goal.status === 'active' && (
          <GoalStatusLabel
            value={goal.statusLabel}
            onChange={(v) => onSetStatusLabel?.(goal.goalId, v)}
          />
        )}
        {goal.status === 'draft' && <Pill tone="neutral">draft</Pill>}
        {goal.status === 'achieved' && <Pill tone="success">achieved</Pill>}
        {goal.status === 'dropped' && <Pill tone="neutral">dropped</Pill>}
      </div>

      <h1
        ref={titleRef}
        contentEditable={isEditable}
        suppressContentEditableWarning
        onBlur={handleTitleBlur}
        style={styles.title}
      >
        {goal.title}
      </h1>

      {(goal.description || isEditable) && (
        <p
          ref={descRef}
          contentEditable={isEditable}
          suppressContentEditableWarning
          onBlur={handleDescBlur}
          style={styles.description}
          data-placeholder="Add a description..."
        >
          {goal.description || ''}
        </p>
      )}

      {/* Lifecycle buttons */}
      <div style={styles.actions}>
        {goal.status === 'draft' && (
          <Button onClick={() => onActivate?.(goal.goalId)}>Activate</Button>
        )}
        {goal.status === 'active' && (
          <>
            <Button onClick={() => onAchieve?.(goal.goalId)}>Mark achieved</Button>
            <Button variant="secondary" onClick={() => setShowDropForm(true)}>Drop</Button>
          </>
        )}
        {(goal.status === 'achieved' || goal.status === 'dropped') && (
          <Button onClick={() => onReopen?.(goal.goalId)}>Reopen</Button>
        )}
      </div>

      {showDropForm && (
        <div style={styles.dropForm}>
          <TextArea
            value={dropReason}
            onChange={(e) => setDropReason(e.target.value)}
            placeholder="Why is this goal being dropped? (min 10 chars)"
            rows={2}
            maxLength={280}
            aria-label="Drop reason"
          />
          {dropError && <Banner tone="error">{dropError}</Banner>}
          <div style={styles.dropActions}>
            <Button
              variant="secondary"
              onClick={() => { setShowDropForm(false); setDropReason('') }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDrop}
              loading={dropping}
              loadingLabel="Dropping…"
            >
              Confirm drop
            </Button>
          </div>
        </div>
      )}

      {goal.status === 'dropped' && goal.droppedReason && (
        <p style={styles.dropReason}>Dropped: {goal.droppedReason}</p>
      )}
    </div>
  )
}

const styles = {
  header: { display: 'flex', flexDirection: 'column', gap: '10px' },
  topRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  badges: { display: 'flex', alignItems: 'center', gap: '6px', flex: 1 },
  typeBadge: { fontSize: '10px', padding: '2px 8px', borderRadius: '8px', fontWeight: 500 },
  horizon: { fontSize: '11px', color: 'var(--text-secondary)' },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    margin: 0,
    outline: 'none',
    lineHeight: 1.3,
    color: 'var(--text-primary)',
  },
  description: {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    margin: 0,
    lineHeight: 1.6,
    outline: 'none',
    minHeight: '20px',
  },
  actions: { display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' },
  dropForm: { display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' },
  dropActions: { display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' },
  dropReason: {
    fontSize: 12,
    color: 'var(--danger-text)',
    background: 'var(--danger-bg)',
    padding: '6px 10px',
    borderRadius: 'var(--radius-md)',
    margin: 0,
  },
}
